import { useState, useRef, type ChangeEvent, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import type { CreateProductRequestDto } from "../../../../api";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { showErrorToast } from "../../../../utils/toasts";
import { api } from "../../../api_client";
import { BulkUploadView } from "../components/bulk-upload-view";
import { saveBulkUploadResults } from "./my-product-bulk-upload-results-page";

export interface ImportError {
  product: string;
  row?: number;
  sku?: string;
  message: string;
  image?: string;
}

export interface PreviewResult {
  data: CreateProductRequestDto[];
  errors: ImportError[];
}

const DEFAULT_PRICING_TIERS = 3;

function buildExpectedHeaders(tierCount = DEFAULT_PRICING_TIERS): string[] {
  const templateCols = [
    "Name", "Brand", "Category", "Description", "ProductType",
    "Warranty", "WarrantyType", "WarrantyProvider", "CountryOfOrigin",
    "ScreenSize", "Resolution", "DatasheetURL", "Notes", "DealType",
    "MainImageURL", "GalleryImageURLs",
  ];
  const tierCols: string[] = [];
  for (let i = 1; i <= tierCount; i++) {
    tierCols.push(`PricingTier_${i}_MinQty`, `PricingTier_${i}_Price`);
  }
  const variantCols = [
    "Storage", "Color", "SKU", "ImageURL", "Quantity", "Model", "Condition",
    "CPU", "RAM", "VariantDatasheetURL", "VariantNotes",
  ];
  return [...templateCols, ...tierCols, ...variantCols];
}

const EXPECTED_HEADERS = buildExpectedHeaders();

export function MyProductBulkUploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging]       = useState(false);
  const [isUploading, setIsUploading]     = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  function handleDragOver(e: DragEvent<HTMLDivElement>) { e.preventDefault(); setIsDragging(true); }
  function handleDragLeave(e: DragEvent<HTMLDivElement>) { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }
  function handleDrop(e: DragEvent<HTMLDivElement>) { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) setSelectedFiles((p) => [...p, f]); }
  function handleFileChange(e: ChangeEvent<HTMLInputElement>) { const f = e.target.files?.[0]; if (f) setSelectedFiles((p) => [...p, f]); e.target.value = ""; }
  function removeFile(index: number) { setSelectedFiles((p) => p.filter((_, i) => i !== index)); }

  async function handleUpload() {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFiles[0]);
      const res = await api.post<PreviewResult>(
        "/api/v1/product-requests/preview-import-excel",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const { data: previews, errors } = res.data;

      if (!previews?.length && !errors?.length) {
        showErrorToast("No products found in the uploaded file.");
        return;
      }

      saveBulkUploadResults({ previews: previews ?? [], errors: errors ?? [] });
      navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS);
    } catch {
      showErrorToast("Failed to parse Excel file. Please verify the format and try again.");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4 max-md:pt-6">

        <div className="flex items-center gap-2">
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconArrowLeft size={18} />}
            onClick={() => navigate(AppRoutes.MY_PRODUCTS)}
            className="!h-auto !px-0 !py-0"
          >
            <Heading variant="h3" tone="inherit">My Products</Heading>
          </Button>
          {/* Phones: the page title right below repeats it. */}
          <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
          <Heading variant="h3" tone="dark" className="max-md:hidden">Bulk Product Upload</Heading>
        </div>

        <Heading variant="h2" tone="dark">Bulk Product Upload</Heading>

        <BulkUploadView
          inputRef={inputRef}
          selectedFiles={selectedFiles}
          isDragging={isDragging}
          isUploading={isUploading}
          expectedHeaders={EXPECTED_HEADERS}
          onFileRemove={removeFile}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileChange={handleFileChange}
          onUpload={handleUpload}
        />

      </div>
    </div>
  );
}

export default MyProductBulkUploadPage;
